import React from 'react';
import { ChevronRight, Instagram, Twitter, Youtube } from 'lucide-react';
import Navbar from '../components/Navbar.jsx';
import Footer from '../components/Footer.jsx';
import Seo from '../components/Seo.jsx';

const A = 'Assets/About us assets/';

const T = 'Assets/Team/';

const teamMembers = [
  { name: 'Mutinda Kilonzo',   role: 'Co-founder, Creative director',                       img: `${T}Mutinda.jpeg` },
  { name: 'Matthias Munene',   role: 'Co-founder, Partnerships/Relationships management',    img: `${T}Mathias.jpeg` },
  { name: 'Keith Angana',      role: 'Lead Lectures Coordinator',                            img: `${T}Angana.jpeg` },
  { name: 'Gaul Saul',         role: 'Assistant Lectures Coordinator',                       img: `${T}Gaul.jpeg` },
  { name: 'Jemimah Mukasa',    role: 'Copy editor',                                          img: `${T}Jemimah.jpeg` },
  { name: 'Ernest Muigai',     role: 'Lead videographer',                                    img: `${T}Ernest.jpeg` },
  { name: 'Tony Kabuthu',      role: 'Lead Photographer',                                    img: `${T}Tony.jpeg` },
  { name: 'Robinson Muhota',   role: 'Quality assessment',                                   img: `${T}Ted.jpeg` },
  { name: 'Ashley',            role: 'Graphic designer',                                     img: `${T}Ashley.jpeg` },
  { name: 'Winstone Odhiambo', role: 'Audience welfare',                                    img: `${T}Winston.jpeg` },
  { name: 'Moses Gathoga',     role: 'Lecture content creator',                              img: `${T}Moses.jpeg` },
];

const avatarPalette = [
  '#E8672E', '#17140F', '#C0392B', '#2C3E50', '#6B4226',
  '#27AE60', '#8E44AD', '#D35400', '#16A085', '#2980B9', '#7F8C8D',
];

function MemberAvatar({ name, index, img }) {
  const initials = name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
  if (img) {
    return (
      <img
        loading="lazy"
        decoding="async"
        src={img}
        alt={name}
        style={{
          width: '96px', height: '96px', borderRadius: '50%',
          objectFit: 'cover', objectPosition: 'center top',
          margin: '0 auto 1.1rem', display: 'block',
          boxShadow: '0 4px 16px rgba(0,0,0,0.13)',
          flexShrink: 0,
        }}
      />
    );
  }
  return (
    <div style={{
      width: '96px', height: '96px', borderRadius: '50%',
      background: avatarPalette[index % avatarPalette.length],
      color: '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: '1.4rem', fontWeight: '800', letterSpacing: '0.02em',
      margin: '0 auto 1.1rem',
      boxShadow: '0 4px 16px rgba(0,0,0,0.13)',
      flexShrink: 0,
    }}>
      {initials}
    </div>
  );
}

export default function AboutUs() {
  return (
    <div style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif", background: '#FFFFFF', minHeight: '100vh', margin: 0 }}>
      <Seo title="About Us" description="The story behind Drunken Lectures — how a bar conversation became a community obsessed with big ideas." path="/about" />
      <Navbar />

      {/* ── HERO ── */}
      <section style={{ paddingTop: '72px', background: '#0D0D0D', position: 'relative', overflow: 'hidden' }}>
        <img src={`${A}us (1).png`} alt="YOU US" style={{ width: '100%', maxHeight: '520px', objectFit: 'cover', objectPosition: 'center', display: 'block' }} />
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '120px', background: 'linear-gradient(to bottom, transparent, #FFFFFF)' }} />
      </section>

      {/* ── THE BEGINNING ── */}
      <section id="beginning" style={{ padding: '6rem 1.5rem', maxWidth: '800px', margin: '0 auto' }}>
        <h2 style={sectionTitle}>The Beginning</h2>
        <p style={body}>
          <strong>1</strong> In the beginning God created the heavens and the earth. <strong>2</strong> Now the earth was formless and empty, anti-intellectual propaganda and shallow conversations were over the surface of Nairobi, and the Spirit of Intellectualism was hovering over the city, looking for a home. The social spaces looked ideal. <strong>3</strong> And God said, "Let there be Drunken Lectures," and there was Drunken Lectures. <strong>4</strong> God saw that the lectures were good.
        </p>
        <p style={{ ...body, marginTop: '1.4rem' }}>
          An idea that sprouted in a gentleman in Nairobi was shared in a social space, sober. Shared to another guy, without drinking they thought, "smart conversations like these should be had more often, better with a drink in hand." A month later, the latter gentleman had a conversation with an open minded cafe/restaurant owner in Nairobi CBD. The lady skeptical but convicted decided to give it a shot. The gentleman had a similar conversation with a smart historian friend, "please come teach guys about some interesting study you have done? In a bar." The sober friend obliged. On the 24th of February, in the dark scary streets of Nairobi, young – thirsty for a drink and knowledge — people came to listen to the historian teach on the History of the second president of Kenya's economic decisions. Shared the nitty gritties of the external influences and managed to convince Kenyans that their most infamous president was not that bad.
        </p>
        <p style={{ ...body, marginTop: '1.4rem' }}>
          This was just the beginning of a new Nairobi. A lot of beautiful feedback from the 130+ members of the audience. You and I know how honest kids and drunk people are. The news spread and spread. The second one was on how to brew beer and the walls of Cafe Amka could not hold. The laughter, the mindblowing information from brewer Tracey, the humour from our audience and the huge number of people in attendance all together. Chairs had to be borrowed and guys volunteered to stand. The city felt it, the city felt the shift and the heaviness of its people grew on it as their brains got fuller.
        </p>
      </section>

      {/* ── THE NOW ── */}
      <section id="now" style={{ padding: '3rem 1.5rem 5rem', maxWidth: '1100px', margin: '0 auto' }}>
        <div style={{ display: 'flex', gap: '4rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 380px' }}>
            <h2 style={sectionTitle}>The now:</h2>
            <p style={body}>
              Nothing much changed after t to the Drunken Lecture you know and love today. The concept stuck and the audience looked forward to seeing us. We had sold out shows of 120+ back to back, growth was banging on our door like crazy. We had to oblige and move to a bigger space. Bid the streets and the home that made us and like Nairobi men moved on to 'something better'. It was not her, it was us. We are going in different directions. It just felt like it was time. The time was just not right for us to be together. A bigger venue, yes. We still sold out, capacity? 250. 250 Nairobians gather twice a month to have conversations with each other, satisfy an intellectual itch, reflect and reimagine.
            </p>
            <p style={{ ...body, marginTop: '1.25rem' }}>
              Conversions are so diverse, range is our middle name and our loyalists often complain about the gift of being educated on the most random stuff. Politics, geopolitics, science, math, philosophy, psychology you name it. The most beautiful part is constantly having ears and minds that care about what we have going on.
            </p>
          </div>
          <div style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <img loading="lazy" decoding="async" src={`${A}download__21_-removebg-preview.png`} alt="Thinking cherubs" style={{ width: '280px', height: 'auto', objectFit: 'contain' }} />
          </div>
        </div>
      </section>

      {/* ── THE HOW ── */}
      <section id="how" style={{ padding: '3rem 1.5rem 5rem', maxWidth: '800px', margin: '0 auto' }}>
        <h2 style={sectionTitle}>The how:</h2>
        <p style={body}>
          A product conceptualized in a community, grows with a community. At the heart of all our lectures is it's people. The people we do this for, you. Everything is curated to feed off our society and feed it right back.
        </p>
        <p style={{ ...body, marginTop: '1.25rem' }}>
          The most important bit is done in a russian roulette manner. We feed off what we get and a bit of looking. We have a public google forms where anyone who wants to lecture about anything just applies. We have had more than 250+ lecturer applications in the last year. There are a lot of passionate and brainy people in this city. We have managed to host only 28 main lectures as of March 2026. We have done 20+ special editions with almost half being a repetition of the main lectures and the other, new lecturers.
        </p>
        <p style={{ ...body, marginTop: '1.25rem' }}>
          In times of dire need, the environment we are in as a society/country determines, we often solicit. We have addressed key issues like the Finance Bill 2025, Police brutality, Christmas, Kenya's Debt and such through looking for experts on the field to come teach.
        </p>
        <p style={{ ...body, marginTop: '1.25rem' }}>
          We usually have a host of the session, one per session, and even volunteer to do it through a public call. We have had 10+ different hosts over time. Guys that use the platform to boost their confidence, exercise their audience engagement craft and generally just offer their contribution to the movement. This has beautiful and we are forever grateful, receive a big kiss from the writer. Consensually and on the cheek of course.
        </p>
      </section>

      {/* ── EVENT PHOTO BAND ── */}
      <div style={{ width: '100%', maxHeight: '420px', overflow: 'hidden' }}>
        <img loading="lazy" decoding="async" src={`${A}1J2A5779.jpg`} alt="Drunken Lectures event" style={{ width: '100%', height: '420px', objectFit: 'cover', display: 'block' }} />
      </div>

      {/* ── THE STRUCTURE — 5 Ts ── */}
      <section id="structure" style={{ padding: '5rem 1.5rem', maxWidth: '800px', margin: '0 auto' }}>
        <h2 style={sectionTitle}>The structure:</h2>
        <p style={body}>
          The 3 hr, sometimes 2 and half?, session is broken into three parts:
        </p>

        <ul style={{ margin: '2rem 0', padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          <li style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
            <span style={{ color: '#E8672E', fontWeight: '800', fontSize: '1.1rem', flexShrink: 0, lineHeight: '1.6' }}>a.</span>
            <p style={{ ...body, margin: 0 }}>
              The first hour we let the audience pour and spew knowledge to us and each other. We give out a bunch of prompts to members of the audience, divided into groups of 10-15. These prompts are centered around the topic and theme of the evening. Funny, witty, sarcastic prompts. We are very intentional with them, we give a space for grown adults to laugh on a Monday, I know I know the lord's work. They discuss the prompt assigned to them in the group and assign the responsibility of sharing their findings to one member of the group. We get a feel of the audience, their perspectives of the topic and they get to teach us and laugh about it. Therapy to be honest.
            </p>
          </li>
          <li style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
            <span style={{ color: '#E8672E', fontWeight: '800', fontSize: '1.1rem', flexShrink: 0, lineHeight: '1.6' }}>b.</span>
            <div style={{ flex: 1 }}>
              <p style={{ ...body, margin: 0 }}>
                We then get to serious business immediately after. You could not tell that these guys just spewed all those jokes and banged tables laughing a few seconds ago. The lecturer is introduced and they give their lecture for 30 min-40 min. The structure of the lecture themselves are usually similar:
              </p>
              <p style={{ ...body, marginTop: '0.75rem', paddingLeft: '1.25rem' }}>
                <strong>i) History</strong> — we are very intentional with how it started, how we got here. Every lecture places historical context on what happened in the past in regards to the topic of the day. We had a lecture on how to run a marathon under 2hrs, scientifically by sports Dr. Oliver, and he gave the history of how marathons even became a thing. And why the number 42? Do you have any ideas?
              </p>
              <p style={{ ...body, marginTop: '0.75rem', paddingLeft: '1.25rem' }}>
                <strong>ii) The Now</strong> — we then bring it to modern day and address the point of the lecture. The lecturer shares their study and findings and what they think is the problem or what is happening in the context of their lecture.
              </p>
              <p style={{ ...body, marginTop: '0.75rem', paddingLeft: '1.25rem' }}>
                <strong>iii) The now what</strong> — The lecture ends with a call to action. What can we do about all you just shared? Is there something we can do? How can we do it and what do you think should change with how we approach this conversation? What was the point of learning all these? A lecture by Gaul Saul on how cars work, ended with him advising the members of the audience on how to choose a car in context of what they are looking for. Eco-friendly? Budget friendly? Luxury? All these with mechanical formulas he had just taught. Now that's an impact, no?
              </p>
            </div>
          </li>
          <li style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
            <span style={{ color: '#E8672E', fontWeight: '800', fontSize: '1.1rem', flexShrink: 0, lineHeight: '1.6' }}>c.</span>
            <p style={{ ...body, margin: 0 }}>
              The next part is usually one of the most looked forward to sessions. The Q &amp; A. Here, the audience gets to engage with the lecturer, on a one on one basis. Anyone can ask questions about any part of the lecture they did not understand, curious about, they wanna challenge or anything between. The lecturer responds and a conversion is had. This is usually open ended and can take anywhere between 20 min - an hour plus.
            </p>
          </li>
          <li style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
            <span style={{ color: '#E8672E', fontWeight: '800', fontSize: '1.1rem', flexShrink: 0, lineHeight: '1.6' }}>d.</span>
            <p style={{ ...body, margin: 0 }}>
              The last small segment is usually a trivia, hosted to test the smart/keen students in class. A small 10 min trivia, and the top three winners awarded as the crowd disperses as they go home. Their hearts, minds, souls and stomachs are full in equal measures.
            </p>
          </li>
        </ul>

        <p style={{ ...body, marginTop: '1.5rem' }}>
          All these happens in a bar and restaurant as the audience eats and drinks and have conversations with strangers.
        </p>
      </section>

      {/* ── THE FUTURE ── */}
      <section id="future" style={{ padding: '3rem 1.5rem 4rem', background: '#F7F4EE' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.5rem' }}>
            <img loading="lazy" decoding="async" src={`${A}future.png`} alt="The Future" style={{ width: '100%', maxWidth: '720px', maxHeight: '480px', objectFit: 'contain', display: 'block' }} />
          </div>
          <div style={{ maxWidth: '800px' }}>
            <h2 style={sectionTitle}>The Future:</h2>
            <p style={body}>
              I am tempted to say, 'is now', because it is. Drunken lectures is committed to continue centering the society, its people and the wellbeing of every human in this city and beyond. We preach the gospel of love as much as we preach intellectualism. We look forward to educating, being part of change that is dire in our communities, being the voice of the people and a tool to sharpen our thoughts, challenge our thinking and fill our cups.
            </p>
            <p style={{ ...body, marginTop: '1.25rem' }}>
              A never ending foundation of good things and knowledge.
            </p>
            <p style={{ ...body, marginTop: '1.25rem' }}>
              Drunken lectures is a product of Lectura collective, an organisation committed to disseminate knowledge and spread it. Reshape the way we think and give room to reimagine better versions of our systems and self.
            </p>
          </div>
        </div>
      </section>

      {/* ── PRODUCTS ── */}
      <section style={{ padding: '5rem 1.5rem', maxWidth: '900px', margin: '0 auto' }}>
        <div style={{ marginBottom: '3rem' }}>
          <p style={{ fontSize: '0.72rem', fontWeight: '700', letterSpacing: '0.12em', color: '#E8672E', textTransform: 'uppercase', margin: '0 0 0.9rem' }}>What we create</p>
          <h2 style={sectionTitle}>Products of Drunken Lectures:</h2>
        </div>

        {/* A. Nyi-robi */}
        <div style={{ marginBottom: '3.5rem', display: 'flex', gap: '3rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 320px' }}>
            <p style={{ fontSize: '0.72rem', fontWeight: '700', letterSpacing: '0.1em', color: '#E8672E', textTransform: 'uppercase', margin: '0 0 0.5rem' }}>A. Nyi-robi</p>
            <h3 style={{ fontSize: '1.6rem', fontWeight: '900', color: '#17140F', margin: '0 0 1.25rem', letterSpacing: '-0.02em' }}>Nyi-robi</h3>
            <p style={body}>
              A contemporary magazine that voices kenyans thoughts on different subjects. The magazine is a compilation of all the different lectures we have done in 2025. More than 15+ collections of think pieces, articles, essays and non-fiction works written by different kenyan thinkers. Available on leading kenyan bookstores.
            </p>
            <a href="https://nuriakenya.com/product/nyi-robi/" target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', color: '#E8672E', fontWeight: '700', fontSize: '0.88rem', marginTop: '1.25rem', textDecoration: 'none' }}>
              Read more <ChevronRight size={15} />
            </a>
          </div>
          <div style={{ flex: '0 0 auto' }}>
            <img loading="lazy" decoding="async" src={`${A}image (1).png`} alt="Nyi-robi magazine" style={{ width: '280px', maxWidth: '100%', borderRadius: '12px', display: 'block' }} />
          </div>
        </div>

        <div style={{ height: '1px', background: '#EDE6D6', marginBottom: '3.5rem' }} />

        {/* B. Top-lectures */}
        <div>
          <p style={{ fontSize: '0.72rem', fontWeight: '700', letterSpacing: '0.1em', color: '#E8672E', textTransform: 'uppercase', margin: '0 0 0.5rem' }}>B. Spin-off</p>
          <h3 style={{ fontSize: '1.6rem', fontWeight: '900', color: '#17140F', margin: '0 0 1.25rem', letterSpacing: '-0.02em' }}>Yap-lectures</h3>
          <p style={body}>
            A spin-off of drunken lectures that is centered on telling people's stories. Hosted once a month, it is the not very serious version of Drunken lectures. Here, the crowd engages and gives lectures (yaps) as well. The first hour, no discussions, just mini lectures for 3-5 min from the audience, related to the theme of the day. A main yap lecture and then a Q&A segment after. Meant to serve as an archive for Kenyan social and political tales.
          </p>
          <a href="https://www.youtube.com/watch?v=XPqGx2rsDMg&list=PLk4ZSpPiBcXZJKWWtfGSlKYxF4cAOnLS_" target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', color: '#E8672E', fontWeight: '700', fontSize: '0.88rem', marginTop: '1.25rem', textDecoration: 'none' }}>
            Watch now <ChevronRight size={15} />
          </a>
        </div>
      </section>

      {/* ── OUR TEAM ── */}
      <section id="team" style={{ padding: '5rem 1.5rem', background: '#F7F4EE' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <div style={{ marginBottom: '3.5rem' }}>
            <p style={{ fontSize: '0.72rem', fontWeight: '700', letterSpacing: '0.12em', color: '#E8672E', textTransform: 'uppercase', margin: '0 0 0.75rem' }}>The People</p>
            <h2 style={sectionTitle}>Our Team</h2>
          </div>
          <div className="team-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1.25rem' }}>
            {teamMembers.map((member, i) => (
              <div key={i} style={{ background: '#FDF0E8', borderRadius: '14px', padding: '2rem 1.5rem', textAlign: 'center', border: '1px solid rgba(232, 103, 46,0.08)' }}>
                <MemberAvatar name={member.name} index={i} img={member.img} />
                <h3 style={{ fontSize: '1.05rem', fontWeight: '800', color: '#17140F', margin: '0 0 0.5rem', lineHeight: 1.3 }}>{member.name}</h3>
                <p style={{ fontSize: '0.88rem', color: '#6B665C', lineHeight: 1.5, margin: 0 }}>{member.role}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── OUR PARTNERS ── */}
      <section id="partners" style={{ padding: '5rem 0', background: 'white', borderTop: '1px solid #EDE6D6', overflow: 'hidden' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 1.5rem', marginBottom: '3rem' }}>
          <p style={{ fontSize: '0.72rem', fontWeight: '700', letterSpacing: '0.12em', color: '#E8672E', textTransform: 'uppercase', margin: '0 0 0.75rem' }}>Working Together</p>
          <h2 style={sectionTitle}>Our Partners</h2>
        </div>
        <div style={{ overflow: 'hidden', width: '100%' }}>
          <div
            className="partner-track"
            style={{ display: 'flex', alignItems: 'center', gap: '5rem', width: 'max-content', animation: 'partnerScroll 24s linear infinite' }}
          >
            {[0, 1].flatMap((set) =>
              ['1', '2', '3', '4', '5', '6', '7'].map((n) => (
                <img
                  loading="lazy"
                  decoding="async"
                  key={`${set}-${n}`}
                  src={`Assets/Partner logo assets/${n}.png`}
                  alt={`Partner ${n}`}
                  style={{ height: '220px', width: 'auto', objectFit: 'contain', opacity: 0.85, flexShrink: 0, filter: 'grayscale(0.2)' }}
                />
              ))
            )}
          </div>
        </div>
      </section>

      {/* ── FOLLOW US ── */}
      <section style={{ padding: '4rem 1.5rem', background: '#17140F', textAlign: 'center' }}>
        <h2 style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Helvetica Neue', Arial, sans-serif", fontSize: 'clamp(1.8rem, 4vw, 2.5rem)', fontWeight: '600', color: 'white', margin: '0 0 2.5rem', letterSpacing: '-0.02em' }}>Follow Us</h2>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '1.25rem', flexWrap: 'wrap' }}>
          {[
            { icon: <Instagram size={20} />, label: 'Instagram', href: 'https://www.instagram.com/thedrunkenlectures.ke/' },
            { icon: <Twitter size={20} />, label: 'Twitter / X', href: 'https://x.com/drunkenlectures' },
            { icon: <Youtube size={20} />, label: 'YouTube', href: 'https://www.youtube.com/@Lecturacollective/featured' },
          ].map(({ icon, label, href }) => (
            <a key={label} href={href} aria-label={label} target="_blank" rel="noopener noreferrer"
              style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.75rem 1.5rem', borderRadius: '50px', border: '1.5px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.8)', textDecoration: 'none', fontWeight: '600', fontSize: '0.9rem', transition: 'all 0.3s ease' }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#E8672E'; e.currentTarget.style.color = '#E8672E'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)'; e.currentTarget.style.color = 'rgba(255,255,255,0.8)'; }}>
              {icon} {label}
            </a>
          ))}
        </div>
      </section>

      <Footer />

      <style>{`
        * { box-sizing: border-box; } body { margin: 0; padding: 0; }
        @media (max-width: 680px) {
          .team-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
        @media (max-width: 420px) {
          .team-grid { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 640px) {
          .partner-track img { height: 120px !important; }
          .partner-track { gap: 2.5rem !important; }
        }
        @keyframes partnerScroll {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .partner-track:hover { animation-play-state: paused; }
      `}</style>
    </div>
  );
}

const sectionTitle = {
  fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Helvetica Neue', Arial, sans-serif",
  fontSize: 'clamp(1.8rem, 4vw, 2.75rem)', fontWeight: '600', color: '#17140F',
  letterSpacing: '-0.02em', lineHeight: 1.2, margin: '0 0 1.5rem',
};

const body = { fontSize: '1rem', color: '#3A3A3A', lineHeight: '1.8', margin: 0 };
